/**
 * Manages pushed data updates.
 *
 * @module app/components/push/domUpdates/DataUpdates
 */
import _ from 'lodash';
import DomManipulator from './DomManipulator';
import SportsDataRepository from '../../SportsDataRepository';
import DomPushAdapter from './DomPushAdapter';

/**
 * Logger.
 *
 * @type {Function}
 *
 * @private
 */
var log = function () {
};

/**
 * Mapping of entity ID prefixes to types.
 *
 * @var {Object}
 *
 * @private
 */
var types = {
    'OB_OU': 'selections',
    'OB_MA': 'markets',
    'OB_EV': 'events',
};

/**
 * Get the type of the node from the id.
 *
 * @param   {String}    nodeId
 *
 * @returns {String}    type of node
 *
 * @private
 */
var getNodeType = function (nodeId) {

    if (typeof nodeId === 'undefined') {
        return '';
    }

    var nodeType = nodeId.substr(0, 5);
    return types.hasOwnProperty(nodeType) ? types[nodeType] : '';
};

/**
 * Determine changes in the model.
 *
 * @param   {Object} node
 *
 * @returns {Object}
 *
 * @private
 */
var inferChangesFromModel = function (node, initialTopic, topic) {

    log('DataUpdates.inferChangesFromModel()');

    var modelNode = SportsDataRepository.update(node, topic);
    var nodeType = getNodeType(node.id);

    if (!modelNode) {
        return false;
    }

    return buildChangeset(modelNode, node, nodeType);
};

/**
 * Construct an object to represent what is changing.
 *
 * @param   {Object}    modelNode
 * @param   {Object}    node
 * @param   {String}    nodeType
 *
 * @returns {Object}
 *
 * @private
 */
var buildChangeset = function (modelNode, node, nodeType) {

    var changes = {
        selections: [],
        markets: [],
        events: [],
    };

    changes[nodeType].push({
        attribute: node.attribute,
        node: modelNode,
    });

    return changes;
};

/**
 *
 * @param nodesChanged
 * @param nodeType
 * @returns {*}
 */
var getPossibleNode = function (nodesChanged, nodeType) {

    if (_.isObject(nodesChanged) && nodesChanged[nodeType]) {

        return nodesChanged[nodeType][0].node.$dom;
    }

    return [];
};

/**
 * Checks if there's a new node to be added an handles it
 *
 * @param {Object} node node to be added
 * @param {Object} nodesChanged object containing the list of changes
 * @param {string} initialTopic initial topic which was subscribed to
 * @param {string} topic current topic for the message
 *
 * @returns {void}
 *
 * @private
 */
var handleNewNode = function (node, nodesChanged, initialTopic, topic) {

    var nodeType = getNodeType(node.id);

    if (isNewNode(node, nodesChanged)) {
        DomManipulator.newNode(
            node,
            nodeType,
            initialTopic,
            topic
        );
    } else if (window.whStatic.config && window.whStatic.config.publishPushUpdates) {
        DomPushAdapter.publish(nodeType, SportsDataRepository.getEntityFromQueue(node.id));
    }
};

/**
 * Checks if we need to process a new node
 *
 * @param node
 * @param nodesChanged
 * @returns {boolean}
 */
var isNewNode = function (node, nodesChanged) {

    return nodesChanged === false && node.attribute === 'displayed' && node.value === 'true';
};

/**
 * Methods to be public
 *
 * @type {Object}
 */
var api = {

    /**
     * Update a node.
     *
     * @param   {Object}    node   node to update
     *
     * @returns {void}
     *
     * @public
     */
    update: function (node, initialTopic, topic) {

        log('DataUpdates.update()');

        /**
         * Infers changes to the dom
         *
         * @returns {void}
         *
         * @private
         */
        var inferChanges = function () {
            var nodeType = getNodeType(node.id), possibleNode, nodesChanged;

            nodesChanged = inferChangesFromModel(node, initialTopic, topic);

            possibleNode = getPossibleNode(nodesChanged, nodeType);

            if (possibleNode && possibleNode.length > 0) {
                DomManipulator.update(nodesChanged, initialTopic, topic);
            } else {
                handleNewNode(node, nodesChanged, initialTopic, topic);
            }

            if (window.whStatic.config && window.whStatic.config.publishDeltaPushUpdates) {
                DomPushAdapter.publish(nodeType, {
                    id: node.id,
                    [node.attribute]: node.value,
                });
            }
        };

        inferChanges();
    },

    /**
     * Exposes the DomManipulator
     *
     * @type    {Object}
     *
     * @public
     */
    DomManipulator: DomManipulator,
};

export default api;
